import React, { useEffect, useState } from "react";
import { Trophy, Play, Pause, Music, Flame } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { usePlayer } from "@/context/PlayerContext";
import { useNowPlaying } from "@/hooks/useNowPlaying";
import NowPlayingBadge from "@/components/NowPlayingBadge";
import { motion } from "framer-motion";

const RANK_COLORS = ["text-amber-400", "text-zinc-300", "text-orange-400"];

export default function TopTracksChart() {
  const { playTrack, current, isPlaying, togglePlay } = usePlayer();
  const [tracks, setTracks] = useState([]);
  const [genres, setGenres] = useState([]);
  const [genre, setGenre] = useState("All");
  const nowPlaying = useNowPlaying();

  useEffect(() => {
    api.get("/charts/genres").then(({ data }) => setGenres(["All", ...data.genres])).catch(() => {});
  }, []);

  const load = (g) => api.get(`/charts/top-tracks${g && g !== "All" ? `?genre=${encodeURIComponent(g)}` : ""}`)
    .then(({ data }) => setTracks(data)).catch(() => {});

  useEffect(() => {
    load(genre);
    const h = () => load(genre);
    window.addEventListener("nu:charts", h);
    const i = setInterval(() => load(genre), 30000);
    return () => { window.removeEventListener("nu:charts", h); clearInterval(i); };
  }, [genre]);

  const top5 = tracks.slice(0, 5);

  return (
    <div className="nu-card p-5" data-testid="top-tracks-chart">
      <div className="flex items-center justify-between mb-1">
        <h3 className="flex items-center gap-2 font-display font-extrabold text-lg"><Trophy className="w-5 h-5 text-amber-400" /> Top Tracks</h3>
        <span className="text-[10px] font-mono-nu uppercase tracking-widest text-emerald-400 flex items-center gap-1"><Flame className="w-3 h-3" /> This Week</span>
      </div>
      <p className="text-xs text-zinc-500 mb-3">Ranked by plays across NEXT UP</p>

      {genres.length > 1 && (
        <div className="flex gap-2 overflow-x-auto hide-scrollbar pb-3 -mx-1 px-1" data-testid="genre-filter">
          {genres.map((g) => (
            <button key={g} onClick={() => setGenre(g)} data-testid={`genre-chip-${g.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={`shrink-0 px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                genre === g ? "bg-emerald-500 text-black" : "bg-white/5 text-zinc-400 hover:text-white hover:bg-white/10"}`}>
              {g}
            </button>
          ))}
        </div>
      )}

      {top5.length === 0 ? (
        <p className="text-sm text-zinc-500 py-6 text-center">No tracks in this genre yet.</p>
      ) : (
        <div className="space-y-1">
          {top5.map((t, i) => {
            const active = current?.id === t.id;
            return (
              <motion.div key={t.id} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}
                className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 transition-colors group" data-testid="chart-row">
                <span className={`w-6 text-center font-display font-black text-lg ${RANK_COLORS[i] || "text-zinc-600"}`}>{i + 1}</span>
                {t.cover_url ? <img src={mediaUrl(t.cover_url)} alt="" className="w-10 h-10 rounded object-cover" />
                  : <span className="w-10 h-10 rounded bg-emerald-500/15 flex items-center justify-center"><Music className="w-4 h-4 text-emerald-400" /></span>}
                <div className="min-w-0 flex-1">
                  <p className={`text-sm font-semibold truncate ${active ? "text-emerald-400" : ""}`}>{t.title}</p>
                  {nowPlaying[t.id] ? <NowPlayingBadge count={nowPlaying[t.id]} />
                    : <p className="text-xs text-zinc-500 truncate font-mono-nu flex items-center gap-1"><Play className="w-3 h-3" /> {t.plays || 0} · {t.artist?.artist_name}</p>}
                </div>
                <button onClick={() => (active ? togglePlay() : playTrack(t, top5))} data-testid="chart-play"
                  className="w-8 h-8 rounded-full bg-white/5 group-hover:bg-emerald-500 group-hover:text-black text-emerald-400 flex items-center justify-center transition-colors shrink-0">
                  {active && isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
                </button>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
